import React, { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Col, Icon, Row, Button } from "../../../../../../components/Component";
import { messageToast } from "../../../../../../utils/Utils";
import * as app_consts from "../../../../../../constants/react_app_consts";
import { BiErrorCircle } from "react-icons/bi";
import TextInput from "../../../../../components/inputs/TextInput";
import { makeEmployeeDashboardApiRequest } from "../../../../../../api/api_helper";
import {
  RETRIEVE_ADJUST_INVENTORY_DATA_API,
  ADJUST_INVENTORY_DATA_API,
} from "../../../../../../api/api_data";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const roundTo = (value, dp = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const p = Math.pow(10, dp);
  return String(Math.round(n * p) / p);
};

const formatDisplayNumber = (value, dp = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  return n.toFixed(dp);
};

const toSentenceCase = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "--";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const cardStyle = {
  borderRadius: "16px",
  background: "#ffffff",
  border: "1px solid #e5e9f2",
};

const softCardStyle = {
  borderRadius: "16px",
  background: "#fbfcff",
  border: "1px solid #e5e9f2",
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const AdjustInventoryModel = ({
  data,
  modal,
  setModalLoading,
  onClose,
  onSuccess,
}) => {
  const [errorVal, setError] = useState("");
  const [user_data, setUserData] = useState(data);

  const [data_loading, setDataLoading] = useState(true);
  const [data_error, setDataError] = useState("");
  const [page_data, setPageData] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [adjustmentType, setAdjustmentType] = useState("");
  const [branch, setBranch] = useState("");
  const [warehouseStorage, setWarehouseStorage] = useState("");
  const [ticketType, setTicketType] = useState("");
  const [packaging, setPackaging] = useState("");

  const [inputMode, setInputMode] = useState("packages");
  const [packageCount, setPackageCount] = useState("");
  const [tonnageMt, setTonnageMt] = useState("");

  const [reason, setReason] = useState("");

  useEffect(() => {
    setUserData(data);
  }, [data]);

  useEffect(() => {
    if (!user_data || !modal) return;

    const fetchAdjustData = async () => {
      setDataLoading(true);
      setDataError("");
      setError("");
      setPageData(null);

      setAdjustmentType("");
      setBranch("");
      setWarehouseStorage("");
      setTicketType("");
      setPackaging("");
      setInputMode("packages");
      setPackageCount("");
      setTonnageMt("");
      setReason("");

      if (!user_data?.product_id) {
        setDataError("Missing product_id. Cannot load adjust inventory form data.");
        setDataLoading(false);
        return;
      }

      try {
        const endpoint = RETRIEVE_ADJUST_INVENTORY_DATA_API(user_data.product_id);
        const res = await makeEmployeeDashboardApiRequest("get", endpoint);
        const payload = res?.data?.data;

        if (!payload) {
          setDataError("Retrieve endpoint returned no data.");
          setDataLoading(false);
          return;
        }

        setPageData(payload);
      } catch (err) {
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.errors?.detail || err?.response?.data?.message;

        setDataError(
          serverMsg || `Failed to load adjust inventory data${status ? ` (HTTP ${status})` : ""}.`
        );
      } finally {
        setDataLoading(false);
      }
    };

    fetchAdjustData();
  }, [user_data, modal]);

  const adjustmentTypeOptions = useMemo(() => {
    const list = page_data?.adjustment_types || [];
    return list.map((x) => ({
      value: x?.value ?? x,
      label: x?.label ?? String(x),
    }));
  }, [page_data]);

  const branchOptions = useMemo(() => {
    const list = page_data?.branch_locations || [];
    return list.map((b) => ({
      value: b?.branch_location_id,
      label: b?.branch_area || b?.branch_physical_address || `Branch ${b?.branch_location_id}`,
      raw: b,
    }));
  }, [page_data]);

  const warehouseStorageOptions = useMemo(() => {
    const selectedBranch = branchOptions.find((b) => String(b.value) === String(branch));
    const storages = selectedBranch?.raw?.warehouse_storages || [];

    return storages.map((s) => ({
      value: s?.warehouse_storage_id,
      label: s?.warehouse_storage_reference_number
        ? `${s.warehouse_storage_reference_number} (${s.warehouse_storage_type || "Storage"})`
        : `Storage ${s?.warehouse_storage_id}`,
      raw: s,
    }));
  }, [branch, branchOptions]);

  const ticketTypeOptions = useMemo(() => {
    const list = page_data?.ticket_types || [];
    return list.map((t) => ({
      value: t?.ticket_type_id,
      label: t?.ticket_type,
      raw: t,
    }));
  }, [page_data]);

  const packagingOptions = useMemo(() => {
    const pumList = page_data?.pum_list || [];
    const out = [];

    pumList.forEach((p) => {
      const unitId = p?.unit_measurement_id;
      const unitLabel = `${p?.unit_measurement_full_name || ""} (${p?.unit_measurement_abbrev || ""})`.trim();
      const details = p?.uom_details || [];

      details.forEach((d, idx) => {
        const packet = d?.packet_size || `${d?.base_weight_in_kilos || ""}kg`;
        out.push({
          value: `${unitId}:${idx}`,
          label: unitLabel ? `${packet} - ${unitLabel}` : packet,
          densityKg: toNum(d?.measurement_in_kilos),
          raw: { pum: p, uom_detail: d },
        });
      });
    });

    return out;
  }, [page_data]);

  const selectedPackaging = useMemo(
    () => packagingOptions.find((o) => String(o.value) === String(packaging)) || null,
    [packagingOptions, packaging]
  );

  const densityKg = useMemo(() => toNum(selectedPackaging?.densityKg), [selectedPackaging]);

  const selectedUomDescription = useMemo(
    () =>
      selectedPackaging?.raw?.pum?.unit_measurement_full_name ||
      selectedPackaging?.raw?.pum?.unit_measurement_abbrev ||
      "",
    [selectedPackaging]
  );

  const selectedPacketSize = useMemo(
    () => selectedPackaging?.raw?.uom_detail?.packet_size || "",
    [selectedPackaging]
  );
  const packageUnitLabel = useMemo(() => {
    const normalizedPacket = String(selectedPacketSize || "").toLowerCase();
    if (normalizedPacket.includes("bag")) return "Bags";
    return "Packages";
  }, [selectedPacketSize]);
  const defaultPackagingValue = useMemo(() => {
    if (!packagingOptions.length) return "";

    const productPacketSize = normalizeText(page_data?.product?.packet_size);
    const productUomDescription = normalizeText(page_data?.product?.uom_description);

    const matchedOption = packagingOptions.find((option) => {
      const packetSize = normalizeText(option?.raw?.uom_detail?.packet_size);
      const unitName = normalizeText(option?.raw?.pum?.unit_measurement_full_name);
      const unitAbbrev = normalizeText(option?.raw?.pum?.unit_measurement_abbrev);

      return (
        (productPacketSize && packetSize === productPacketSize) ||
        (productUomDescription &&
          (packetSize === productUomDescription ||
            unitName === productUomDescription ||
            unitAbbrev === productUomDescription))
      );
    });

    if (matchedOption) {
      return String(matchedOption.value);
    }

    if (packagingOptions.length === 1) {
      return String(packagingOptions[0].value);
    }

    return "";
  }, [packagingOptions, page_data]);

  const isPhysicalAdjustment = Number(ticketType) === 2;

  const selectedAdjustmentLabel = useMemo(
    () => adjustmentTypeOptions.find((option) => String(option.value) === String(adjustmentType))?.label || "--",
    [adjustmentType, adjustmentTypeOptions]
  );

  const selectedTicketTypeLabel = useMemo(
    () => ticketTypeOptions.find((option) => String(option.value) === String(ticketType))?.label || "--",
    [ticketType, ticketTypeOptions]
  );

  const selectedBranchLabel = useMemo(
    () => branchOptions.find((option) => String(option.value) === String(branch))?.label || "--",
    [branch, branchOptions]
  );

  const selectedWarehouseLabel = useMemo(
    () =>
      warehouseStorageOptions.find((option) => String(option.value) === String(warehouseStorage))?.label || "--",
    [warehouseStorage, warehouseStorageOptions]
  );

  const hasQuantityInput = Number(packageCount) > 0 || Number(tonnageMt) > 0;

  const adjustmentDirectionText = useMemo(() => {
    const normalizedType = String(adjustmentType || "").toLowerCase();
    if (normalizedType.includes("add")) return "Adding to stock";
    if (
      normalizedType.includes("subtract") ||
      normalizedType.includes("deduct") ||
      normalizedType.includes("remove")
    ) {
      return "Removing from stock";
    }
    return "Stock update";
  }, [adjustmentType]);

  useEffect(() => {
    setWarehouseStorage("");
  }, [branch]);

  useEffect(() => {
    setPackageCount("");
    setTonnageMt("");
  }, [packaging]);

  useEffect(() => {
    if (packaging || !defaultPackagingValue) return;
    setPackaging(defaultPackagingValue);
  }, [defaultPackagingValue, packaging]);

  useEffect(() => {
    if (!page_data || !user_data?.warehouse_storage_id || branch) return;

    const matchedBranch = branchOptions.find((option) =>
      (option.raw?.warehouse_storages || []).some(
        (storage) => String(storage?.warehouse_storage_id) === String(user_data.warehouse_storage_id)
      )
    );

    if (matchedBranch) {
      setBranch(String(matchedBranch.value));
    }
  }, [branch, branchOptions, page_data, user_data]);

  useEffect(() => {
    if (!user_data?.warehouse_storage_id || !warehouseStorageOptions.length || warehouseStorage) return;

    const matchedStorage = warehouseStorageOptions.find(
      (option) => String(option.value) === String(user_data.warehouse_storage_id)
    );

    if (matchedStorage) {
      setWarehouseStorage(String(matchedStorage.value));
    }
  }, [user_data, warehouseStorage, warehouseStorageOptions]);

  useEffect(() => {
    if (!densityKg || densityKg <= 0) return;

    if (inputMode === "packages") {
      if (packageCount === "") {
        setTonnageMt("");
        return;
      }

      const pk = Number(packageCount);
      if (!Number.isFinite(pk) || pk < 0) {
        setTonnageMt("");
        return;
      }

      const kg = pk * densityKg;
      const mt = kg / 1000;
      setTonnageMt(roundTo(mt, 3));
      return;
    }

    if (inputMode === "tonnage") {
      if (tonnageMt === "") {
        setPackageCount("");
        return;
      }

      const mt = Number(tonnageMt);
      if (!Number.isFinite(mt) || mt < 0) {
        setPackageCount("");
        return;
      }

      const kg = mt * 1000;
      const pk = densityKg ? kg / densityKg : 0;
      setPackageCount(roundTo(pk, 3));
    }
  }, [inputMode, packageCount, tonnageMt, densityKg]);

  const submitDisabled =
    !adjustmentType ||
    !ticketType ||
    !packaging ||
    !reason.trim() ||
    !densityKg ||
    !selectedPacketSize ||
    Number(packageCount) <= 0 ||
    (isPhysicalAdjustment && (!branch || !warehouseStorage));

  const submitAdjustment = async (e) => {
    e.preventDefault();
    setError("");

    if (submitDisabled) {
      messageToast("Fill in all required fields to proceed.", app_consts.FAIL);
      return;
    }

    try {
      setSubmitLoading(true);
      setModalLoading?.(true);

      const payload = {
        product_id: Number(user_data?.product_id),
        capacity: Number(packageCount),
        inventory_type_id: Number(ticketType),
        reason: reason.trim(),
        uom_description: selectedPacketSize,
        adjustment_type: String(adjustmentType),
      };

      if (isPhysicalAdjustment) {
        payload.warehouse_storage_id = Number(warehouseStorage);
        payload.branch_location_id = Number(branch);
      }

      const res = await makeEmployeeDashboardApiRequest("post", ADJUST_INVENTORY_DATA_API, payload);

      messageToast(res?.data?.message || "Inventory Data Adjusted Successfully.", app_consts.SUCCESS);

      onSuccess?.(res?.data);
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.errors?.detail ||
        err?.response?.data?.message ||
        "Failed to adjust inventory.";

      console.log("POST ADJUST ERROR >>>", { status, serverMsg }, err);
      setError(serverMsg);
      messageToast(serverMsg, app_consts.FAIL);
    } finally {
      setSubmitLoading(false);
      setModalLoading?.(false);
    }
  };

  const productName = page_data?.product?.product_name || "";
  const productType = page_data?.product?.product_type || "";

  const renderSelect = ({
    id,
    label,
    value,
    onChange,
    options,
    placeholder,
    required = true,
    disabled = false,
    helperText = "",
  }) => (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      <div className="form-control-wrap">
        <select
          id={id}
          className="form-select"
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          style={{ borderRadius: "10px" }}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {helperText ? <small className="text-muted">{helperText}</small> : null}
    </div>
  );

  return (
    <>
      <div
        className="mb-4 p-3"
        style={{
          borderRadius: "16px",
          background: "linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)",
          border: "1px solid #dbe7ff",
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h5 className="card-title mb-1">Adjust Inventory</h5>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <span className="badge badge-dim bg-primary">
              Product: <span className="ms-1">{productName || "--"}</span>
            </span>
            <span className="badge badge-dim bg-light text-dark border">
              Type: <span className="ms-1">{productType || "--"}</span>
            </span>
          </div>
        </div>
      </div>

      {errorVal && (
        <div className="mb-3 w-100">
          <Alert color="danger" className="alert-icon" style={{ borderRadius: "12px", marginBottom: 0 }}>
            <Icon name="alert-circle" /> {errorVal}
          </Alert>
        </div>
      )}

      {data_loading ? (
        <div
          className="w-full p-5 d-flex flex-column justify-content-center align-items-center"
          style={{
            minHeight: "260px",
            borderRadius: "14px",
            background: "#fafbff",
            border: "1px solid #e5e9f2",
          }}
        >
          <Spinner />
          <span className="mt-2 fs-16px">Loading Adjust Inventory Data...</span>
        </div>
      ) : data_error ? (
        <div
          className="mt-3 p-5 text-soft alert-icon text-center fw-bold fs-15px"
          style={{
            borderRadius: "14px",
            background: "#fff7f7",
            border: "1px solid #f5c2c7",
          }}
        >
          <BiErrorCircle className="text-danger fs-3" />
          <p className="mb-0 mt-2">{data_error}</p>
        </div>
      ) : (
        <form onSubmit={submitAdjustment}>
          <Row className="g-4 mt-1">
            <Col xl="7">
              <div className="h-100 p-4" style={cardStyle}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="fw-bold fs-16px">Adjustment Setup</div>
                    <div className="text-soft small">
                      Choose how and where this stock change should be applied.
                    </div>
                  </div>
                  <span className="badge badge-dim bg-light text-dark border">{adjustmentDirectionText}</span>
                </div>

                <Row className="g-3">
                  <TextInput
                    label="Product Name"
                    required
                    type="text"
                    id_val="product_name"
                    maxlength="100"
                    value={productName}
                    colSize={6}
                    name="product_name"
                    disabled
                  />

                  <TextInput
                    label="Product Type"
                    required={false}
                    type="text"
                    id_val="product_type"
                    maxlength="100"
                    value={productType}
                    colSize={6}
                    name="product_type"
                    disabled
                  />

                  <Col md="6">
                    {renderSelect({
                      id: "adjustment_type",
                      label: "Adjustment Type",
                      value: adjustmentType,
                      onChange: (e) => setAdjustmentType(e.target.value),
                      options: adjustmentTypeOptions,
                      placeholder: "Select adjustment type",
                    })}
                  </Col>

                  <Col md="6">
                    {renderSelect({
                      id: "ticket_type",
                      label: "Inventory Type",
                      value: ticketType,
                      onChange: (e) => setTicketType(e.target.value),
                      options: ticketTypeOptions,
                      placeholder: "Select inventory type",
                    })}
                  </Col>

                  {isPhysicalAdjustment ? (
                    <>
                      <Col md="6">
                        {renderSelect({
                          id: "branch",
                          label: "Branch",
                          value: branch,
                          onChange: (e) => setBranch(e.target.value),
                          options: branchOptions,
                          placeholder: "Select branch",
                        })}
                      </Col>

                      <Col md="6">
                        {renderSelect({
                          id: "warehouse_storage",
                          label: "Warehouse Storage",
                          value: warehouseStorage,
                          onChange: (e) => setWarehouseStorage(e.target.value),
                          options: warehouseStorageOptions,
                          placeholder: branch ? "Select warehouse storage" : "Select branch first",
                          disabled: !branch,
                          helperText:
                            branch && warehouseStorageOptions.length === 0
                              ? "This branch has no warehouse storages returned by the API."
                              : "",
                        })}
                      </Col>
                    </>
                  ) : null}

                  <Col md="12">
                    <div className="form-group mb-0">
                      <label className="form-label" htmlFor="reason">
                        Reason / Message <span className="text-danger">*</span>
                      </label>
                      <div className="form-control-wrap">
                        <textarea
                          id="reason"
                          className="form-control"
                          rows={4}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="e.g. Adjustment after physical count, reconciliation, damage, spill, or transfer..."
                          required
                          style={{ borderRadius: "10px", resize: "none" }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col xl="5">
              <div className="h-100 p-4" style={softCardStyle}>
                <div className="mb-3">
                  <div className="fw-bold fs-16px">Packaging and Conversion</div>
                  <div className="text-soft small">
                    Enter packages or MT and the form will calculate the equivalent quantity using the selected package size.
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="packaging">
                    Unit / Packaging <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <select
                      id="packaging"
                      className="form-select"
                      value={packaging}
                      onChange={(e) => setPackaging(e.target.value)}
                      required
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="" disabled>
                        Select unit / packaging
                      </option>
                      {packagingOptions.map((o) => (
                        <option key={String(o.value)} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <small className="text-muted">
                    {packaging
                      ? `Conversion is currently based on ${selectedPacketSize || "the selected package"} at ${formatDisplayNumber(
                          densityKg,
                          3
                        )} kg each.`
                      : "Select the package size attached to this product before entering tonnage or package count."}
                  </small>
                </div>

                {packaging ? (
                  <div
                    className="mb-3 p-3"
                    style={{
                      borderRadius: "12px",
                      background: "#ffffff",
                      border: "1px solid #e5e9f2",
                    }}
                  >
                    <Row className="g-2">
                      <Col sm="6">
                        <div className="text-soft small text-uppercase">Packet Size</div>
                        <div className="fw-bold">{selectedPacketSize || "--"}</div>
                      </Col>
                      <Col sm="6">
                        <div className="text-soft small text-uppercase">Weight Per Package</div>
                        <div className="fw-bold">{formatDisplayNumber(densityKg, 3)} kg</div>
                      </Col>
                      <Col sm="12">
                        <div className="text-soft small text-uppercase">Unit</div>
                        <div className="fw-bold">{selectedUomDescription || "--"}</div>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <div
                    className="mb-3 p-3 text-soft small"
                    style={{
                      borderRadius: "12px",
                      background: "#ffffff",
                      border: "1px dashed #d7deeb",
                    }}
                  >
                    Select packaging to enable bags-to-tonnage conversion.
                  </div>
                )}

                <div
                  className="p-3 mb-3"
                  style={{
                    borderRadius: "12px",
                    background: "#ffffff",
                    border: "1px solid #e5e9f2",
                  }}
                >
                  <div className="form-group mb-0">
                    <label className="form-label">Input Mode</label>
                    <div className="d-flex gap-4 flex-wrap">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="input_mode"
                          id="mode_packages"
                          checked={inputMode === "packages"}
                          onChange={() => setInputMode("packages")}
                          disabled={!packaging}
                        />
                        <label className="form-check-label" htmlFor="mode_packages">
                          Enter {packageUnitLabel}
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="input_mode"
                          id="mode_tonnage"
                          checked={inputMode === "tonnage"}
                          onChange={() => setInputMode("tonnage")}
                          disabled={!packaging}
                        />
                        <label className="form-check-label" htmlFor="mode_tonnage">
                          Enter MT
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="package_count">
                        Number of {packageUnitLabel}
                        {selectedPacketSize ? ` (${selectedPacketSize})` : ""} <span className="text-danger">*</span>
                      </label>
                      <div className="form-control-wrap">
                        <input
                          id="package_count"
                          className="form-control"
                          type="number"
                          min="0"
                          step="0.001"
                          value={packageCount}
                          onChange={(e) => {
                            setInputMode("packages");
                            setPackageCount(e.target.value);
                          }}
                          placeholder="e.g. 6.600"
                          disabled={!packaging || inputMode !== "packages"}
                          required
                          style={{ borderRadius: "10px" }}
                        />
                      </div>
                      <small className="text-muted">
                        {inputMode !== "packages"
                          ? "Auto-calculated from MT"
                          : `Decimals allowed for partial ${packageUnitLabel.toLowerCase()} equivalents`}
                      </small>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="tonnage_mt">
                        Tonnage (MT) <span className="text-danger">*</span>
                      </label>
                      <div className="form-control-wrap">
                        <input
                          id="tonnage_mt"
                          className="form-control"
                          type="number"
                          min="0"
                          step="0.001"
                          value={tonnageMt}
                          onChange={(e) => {
                            setInputMode("tonnage");
                            setTonnageMt(e.target.value);
                          }}
                          placeholder="e.g. 0.330"
                          disabled={!packaging || inputMode !== "tonnage"}
                          required
                          style={{ borderRadius: "10px" }}
                        />
                      </div>
                      <small className="text-muted">
                        {inputMode !== "tonnage"
                          ? "Auto-calculated from bags"
                          : "Decimals allowed up to 3 decimal places"}
                      </small>
                    </div>
                  </Col>
                </Row>

                <div
                  className="mt-2 p-3"
                  style={{
                    borderRadius: "14px",
                    background: hasQuantityInput ? "#effaf4" : "#ffffff",
                    border: hasQuantityInput ? "1px solid #b8e3c8" : "1px solid #e5e9f2",
                  }}
                >
                  <div className="text-soft small text-uppercase mb-2">Live Summary</div>
                  <div className="fw-bold mb-1">
                    {formatDisplayNumber(tonnageMt || 0, 3)} MT = {formatDisplayNumber(packageCount || 0, 3)}{" "}
                    {packageUnitLabel.toLowerCase()}
                  </div>
                  <div className="small text-soft">
                    {packaging
                      ? `${formatDisplayNumber(densityKg, 3)} kg per package using ${selectedPacketSize || "selected packaging"}`
                      : "Select packaging to preview the conversion summary."}
                  </div>
                  {packaging ? (
                    <div className="small text-soft mt-2">
                      Formula:
                      {` ${formatDisplayNumber(tonnageMt || 0, 3)} MT x 1000 / ${formatDisplayNumber(
                        densityKg,
                        3
                      )} kg = ${formatDisplayNumber(packageCount || 0, 3)} ${packageUnitLabel.toLowerCase()}`}
                    </div>
                  ) : null}
                  <hr className="my-3" />
                  <Row className="g-2">
                    <Col sm="6">
                      <div className="text-soft small text-uppercase">Adjustment</div>
                      <div className="fw-bold">{toSentenceCase(selectedAdjustmentLabel)}</div>
                    </Col>
                    <Col sm="6">
                      <div className="text-soft small text-uppercase">Inventory Type</div>
                      <div className="fw-bold">{toSentenceCase(selectedTicketTypeLabel)}</div>
                    </Col>
                    {isPhysicalAdjustment ? (
                      <>
                        <Col sm="6">
                          <div className="text-soft small text-uppercase">Branch</div>
                          <div className="fw-bold">{selectedBranchLabel}</div>
                        </Col>
                        <Col sm="6">
                          <div className="text-soft small text-uppercase">Storage</div>
                          <div className="fw-bold">{selectedWarehouseLabel}</div>
                        </Col>
                      </>
                    ) : null}
                  </Row>
                </div>
              </div>
            </Col>

            <Col xl="12" className="d-flex justify-content-end mt-4 mr-2">
              <Button
                type="button"
                className="btn btn-dim btn-outline-secondary"
                onClick={() => onClose?.()}
                disabled={submitLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                className="ms-2"
                disabled={submitLoading || submitDisabled}
              >
                {submitLoading ? <Spinner size="sm" color="light" /> : "Save Adjustment"}
              </Button>
            </Col>
          </Row>
        </form>
      )}
    </>
  );
};

export default AdjustInventoryModel;
